import { useState } from 'react';
import { api, ApiError, type Book, type Position, type Side } from '../lib/api';
import { costOf, credits, payoutOf } from '../lib/format';
import { askFor } from './DepthLadder';

/**
 * The order ticket — the most important element in the product.
 *
 * Binary contracts are not intuitive. Someone who has never traded one cannot
 * be expected to work out from "buy 10 YES at 60" what they are risking. So the
 * ticket states, before the button is pressed and in plain numbers: what it
 * costs, what comes back if they are right, and what is lost if they are wrong.
 *
 * Two deliberate absences:
 *
 *  - No leverage, and no way to enter one. Maximum loss is the cost shown, and
 *    keeping that true is worth more than any feature.
 *  - The word "sell short" appears nowhere. Selling YES you do not hold is
 *    identical to buying NO, so the UI offers the buy and lets the engine's
 *    normalisation do the rest. Users get the capital-efficient interpretation
 *    without having to learn what it is.
 */
export function OrderTicket({
  marketId,
  book,
  position,
  disabled,
  onDone,
}: {
  marketId: string;
  book: Book;
  position: Position[];
  disabled?: string;
  onDone: () => void;
}) {
  const [side, setSide] = useState<Side>('YES');
  const [qtyText, setQtyText] = useState('10');
  const [priceText, setPriceText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const held = position.find((p) => p.side === side)?.qty ?? 0;
  const marketAsk = askFor(book, side);
  const qty = Number.parseInt(qtyText, 10);
  const price = priceText === '' ? marketAsk : Number.parseInt(priceText, 10);

  const validQty = Number.isInteger(qty) && qty > 0;
  const validPrice = price !== null && Number.isInteger(price) && price >= 1 && price <= 99;
  const ready = validQty && validPrice && !disabled;

  const cost = validQty && validPrice ? costOf(price, qty) : 0n;
  const payout = validQty ? payoutOf(qty) : 0n;
  const profit = payout - cost;

  async function submit() {
    if (!ready || price === null) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      // An idempotency key means a double-click, a flaky connection or an
      // impatient retry cannot produce two orders.
      const result = await api.placeOrder(
        marketId,
        { direction: 'BUY', side, price, qty, tif: 'GTC' },
        crypto.randomUUID(),
      );
      if (result.accepted) {
        const filled = result.fills.reduce((n, f) => n + f.qty, 0);
        setDone(
          filled === qty
            ? `Filled ${filled} contracts.`
            : filled > 0
              ? `Filled ${filled} of ${qty}; the rest is resting on the book.`
              : 'Order placed on the book.',
        );
        onDone();
      } else {
        setError(explain(result.reason));
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not reach the exchange.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card ticket">
      <h2>Place an order</h2>

      <div className="sidetoggle" role="group" aria-label="Choose a side">
        {(['YES', 'NO'] as const).map((s) => {
          const ask = askFor(book, s);
          return (
            <button
              key={s}
              data-side={s}
              data-active={side === s}
              aria-pressed={side === s}
              onClick={() => {
                setSide(s);
                setPriceText('');
                setError(null);
                setDone(null);
              }}
            >
              <b>{s}</b>
              <small>{ask === null ? 'no offers' : `${ask}%`}</small>
            </button>
          );
        })}
      </div>

      <div className="field">
        <label htmlFor="qty">Contracts</label>
        <input
          id="qty"
          inputMode="numeric"
          value={qtyText}
          onChange={(e) => setQtyText(e.target.value.replace(/[^0-9]/g, ''))}
        />
        {held > 0 && <div className="hint">You hold {held.toLocaleString()} {side}.</div>}
      </div>

      <div className="field">
        <label htmlFor="price">Limit price</label>
        <input
          id="price"
          inputMode="numeric"
          placeholder={marketAsk === null ? '1–99' : `${marketAsk} (market)`}
          value={priceText}
          onChange={(e) => setPriceText(e.target.value.replace(/[^0-9]/g, ''))}
        />
        <div className="hint">
          The most you will pay per contract, as a percentage. You may fill cheaper, never dearer.
        </div>
      </div>

      <div className="payoff">
        <div>
          <span>Cost</span>
          <b>{credits(cost)}</b>
        </div>
        <div className="win">
          <span>If {side} is right</span>
          <b>+{credits(profit)}</b>
        </div>
        <div className="lose">
          <span>If {side} is wrong</span>
          <b>−{credits(cost)}</b>
        </div>
      </div>

      <button className="submit" data-side={side} disabled={!ready || busy} onClick={submit}>
        {busy ? 'Placing…' : `Buy ${side}`}
      </button>

      {disabled && <div className="notice error">{disabled}</div>}
      {error && <div className="notice error">{error}</div>}
      {done && <div className="notice ok">{done}</div>}
    </div>
  );
}

/** Engine rejection codes are precise but not English. */
function explain(reason: string): string {
  switch (reason) {
    case 'INSUFFICIENT_FUNDS':
      return 'Not enough credits for this order.';
    case 'MARKET_NOT_OPEN':
      return 'This market has closed for trading.';
    case 'INVALID_PRICE':
      return 'Price must be a whole number between 1 and 99.';
    case 'INVALID_QTY':
      return 'Enter a whole number of contracts.';
    case 'FOK_UNFILLABLE':
      return 'Not enough available to fill the whole order at once.';
    default:
      return `Order rejected (${reason}).`;
  }
}
